import { useState, useEffect, useContext, useMemo } from "react";
import { SiteContext } from "../contexts/SiteContext";
import fetchSite from "../utils/fetchSite";
import { useParams, Link } from "react-router-dom";
import { Paper, Typography, makeStyles, Fade } from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import { DataGrid } from "@material-ui/data-grid";
import AdminBar from "../components/AdminBar";

function Pages() {
	const [loading, setLoading] = useState(true);
	const [pages, setPages] = useState();
	const { siteSlug } = useParams();
	const { selectedSite, setSelectedSite } = useContext(SiteContext);
	const [sortModel, setSortModel] = useState([{ field: "title", sort: "asc" }]);

	const useStyles = makeStyles((theme) => ({
		root: {
			flex: 1,
			paddingBottom: "4rem",
		},
		main: {
			margin: "0 auto",
			padding: "1rem",
			paddingTop: 0,
			maxWidth: process.env.REACT_APP_MAX_WIDTH,
		},
		paper: {
			padding: "1rem",
			type: "dark",
		},
		title: {
			color:
				selectedSite && selectedSite.secondaryColor
					? selectedSite.secondaryColor
					: theme.palette.secondary.main,
		},
		datagrid: {
			"& a": {
				textDecoration: "none",
				color: theme.palette.primary.main,
			},
			"& a:visited": {
				textDecoration: "none",
				color: theme.palette.primary.main,
			},
			"& a:hover": {
				textDecoration: "underline",
			},
			//Remove outlines when clicking a cell in the datagrid
			"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
				outline: 0,
			},
			//Remove outlines when clicking a column header
			"&.MuiDataGrid-root .MuiDataGrid-colCell:focus": {
				outline: 0,
			},
		},
		subPageMenu: {
			width: "300px",
		},
	}));

	const classes = useStyles();

	// This effect sets the selectedSite based on the :siteSlug (first url parameter)
	useEffect(() => {
		fetchSite(siteSlug, setSelectedSite);
	}, [siteSlug, setSelectedSite]);

	// This effect sets the pages and uses the selectedSite._id to make sure it's on the right site
	useEffect(() => {
		async function fetchPages() {
			let res = await fetch(
				`${process.env.REACT_APP_API_URL}/pages?&site=${selectedSite._id}`,
				{ credentials: "include" }
			);
			res = await res.json();
			if (res.success) {
				const pagesNoChildren = res.data.filter(
					(page) => page.parent === null || page.parent === undefined
				);
				setPages(pagesNoChildren);
				setLoading(false);
			} else {
				console.error(res.error);
			}
		}
		if (selectedSite) {
			fetchPages();
		}
	}, [selectedSite]);

	const columns = useMemo(
		() => [
			{
				field: "title",
				headerName: "Title",
				renderCell: (params) => {
					return (
						<Link to={`/${selectedSite.slug}/pages/${params.row.slug}`}>
							{params.row.title}
						</Link>
					);
				},
				width: 500,
			},
		],
		[selectedSite]
	);

	if (loading || !selectedSite || !pages) {
		return <Loading />;
	}

	return (
		<Fade in={typeof pages != "undefined"}>
			<div className={classes.root}>
				<AdminBar />
				<Header />
				<AppBar />
				<Breadcrumbs
					links={[
						{
							name: "Pages",
							url: `/${selectedSite.slug}/pages`,
						},
					]}
				/>
				<main className={classes.main}>
					<Paper className={classes.paper}>
						<Typography
							className={classes.title}
							gutterBottom
							component="h2"
							variant="h6"
						>
							<LayersIcon /> All Pages
						</Typography>
						<DataGrid
							className={classes.datagrid}
							getRowId={(row) => row._id}
							autoHeight
							rows={pages}
							columns={columns}
							sortModel={sortModel}
							onSortModelChange={(model) => setSortModel(model)}
							disableSelectionOnClick
						/>
					</Paper>
				</main>
			</div>
		</Fade>
	);
}

export default Pages;
